import React from 'react';
import _ from 'lodash';
import { CashieringView, Transfer, AchRelationship, TransferData, ClearerType } from '@tradingblock/types';
import { formatNumber } from '@tradingblock/components';
import {
  getFormattedAchRelationship,
  getFormattedTransferIRAContributionType,
  isNotYetCreatedOrPendingStatus,
} from '../../utilities/cashiering';
import { CashieringEntityOwnerNames } from './CashieringEntityOwnerNames';
import { cleanDateModel } from '../../utilities/date';
import { useClearer } from '../../hooks/useClearer';
import styled from 'styled-components';

// Styled components for grid layout
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--gap);
  margin-bottom: var(--gap);

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  /* Ensure sections align properly */
  > * {
    align-self: start;
  }
`;

const GridSection = styled.div`
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease-in-out;
  height: fit-content;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
`;

const GridSectionHeader = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
  border-bottom: 1px solid var(--lighten);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

const GridSectionContent = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
  color: #fff;

  /* Consistent text styling */
  label {
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
    font-size: 0.98rem;
  }

  .mute {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.98rem;
    font-weight: 400;
  }

  .warn {
    background: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.3);
    border-radius: var(--radius);
    padding: calc(var(--gap) / 3);
    margin: calc(var(--gap) / 3) 0;
    color: var(--orange);
    font-size: 0.9rem;
  }

  .btn-link {
    background: none;
    border: none;
    color: var(--blue);
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
    margin: 0;
    font-size: inherit;

    &:hover {
      color: var(--teal);
    }
  }

  /* Consistent spacing */
  > div {
    margin-bottom: 0;
  }

  > div:last-child {
    margin-bottom: 0;
  }

  /* Better spacing for nested elements */
  .mb-3 {
    margin-bottom: calc(var(--gap) / 2);
  }

  .mt-3 {
    margin-top: calc(var(--gap) / 2);
  }

  /* Ensure consistent value styling */
  span:not(.mute):not([style*='color: var(--teal)']):not([style*='color: var(--orange)']):not([style*='color: var(--green')]) {
    color: rgba(255, 255, 255, 0.85);
    font-weight: 400;
  }

  /* Fix descriptive text styling */
  .txt-sm {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
    font-weight: 400;
  }

  /* Consistent fields-title styling */
  .fields-title {
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  /* Override prop-title and prop-value styling for cashiering */
  .prop-title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
  }

  .prop-value {
    font-size: 0.98rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }

  /* Ensure prop-stacked layout works correctly */
  .prop-stacked {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

// Legacy section styling for backward compatibility
const LegacySection = styled.div`
  margin-bottom: var(--gap);
`;

interface AccountDepositDisplayProps {
  nickname: string;
  availableForWithdrawal: number;
  positions?: number;
  transfer?: TransferData;
  achRelationship: AchRelationship | undefined;
  adminReview?: boolean;
  isPendingFullWithdrawal?: boolean;
  gotoNextView?: (view: CashieringView) => void;
  account?: any; // Add this line for the account prop
  useGridLayout?: boolean; // New prop for grid layout
}

export const AccountDepositDisplay: React.FC<AccountDepositDisplayProps> = ({
  nickname,
  availableForWithdrawal,
  positions,
  transfer,
  achRelationship,
  adminReview,
  gotoNextView,
  children,
  account,
  useGridLayout = false,
}) => {
  const clearer = useClearer();

  const renderDepositAmount = () => (
    <>
      <p>
        <span>Depositing to:</span> <span className="mute">{nickname}.</span> <span>Current balance:</span>{' '}
        <span style={{ fontWeight: 600, fontSize: '1.15em', color: 'var(--teal)' }}>
          {formatNumber(availableForWithdrawal, { currency: true })}
        </span>
        .{' '}
        {!_.isUndefined(positions) && positions !== 0 && (
          <>
            <span>Positions:</span> <span className="mute">{formatNumber(positions, { currency: true })}</span>.
          </>
        )}
      </p>

      <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap' }}>
        <span style={{ whiteSpace: 'nowrap' }}>Amount:</span>
        {transfer && transfer.amount && (
          <span style={{ fontWeight: 700, fontSize: '1.2em', color: 'var(--teal)', whiteSpace: 'nowrap' }}>
            {formatNumber(transfer.amount, { currency: true })}
          </span>
        )}
      </p>

      {gotoNextView && (
        <p>
          <button type="button" className="btn-link" onClick={() => gotoNextView(CashieringView.Type)}>
            <i className="fal fa-pencil" /> Change
          </button>
        </p>
      )}
    </>
  );

  const renderScheduledTransferDetails = () => (
    <>
      <div className="row">
        <div className="col-6">
          <span>Frequency:</span>
          <br />
          <span className="mute">
            Every {transfer && transfer.frequencyInterval}{' '}
            {transfer && transfer.frequencyUnit && transfer.frequencyUnit.toLowerCase()}
            {transfer && transfer.frequencyInterval && transfer.frequencyInterval > 1 ? 's' : ''}
          </span>
          <br />
          <br />
          <span>Start Date:</span>
          <br />
          <span className="mute">
            {transfer && transfer.startDate ? cleanDateModel(transfer.startDate) : 'Not specified'}
          </span>
          {transfer && transfer.occurrences && (
            <>
              <br />
              <br />
              <span>Number of Occurrences:</span>
              <br />
              <span className="mute">{transfer.occurrences}</span>
            </>
          )}
        </div>
      </div>
    </>
  );

  const renderIRAContribution = () => (
    <>
      <div className="row">
        <div className="col-6">
          <div>
            <span className="prop-title">Type:</span>{' '}
            <span className="mute">
              {transfer &&
                transfer.iraContribution &&
                getFormattedTransferIRAContributionType(transfer.iraContribution.type)}
            </span>
          </div>
          <div>
            <span className="prop-title">Contribution Year:</span>{' '}
            <span className="mute">
              {transfer && transfer.iraContribution && transfer.iraContribution.applyToPriorYear === true
                ? new Date().getFullYear() - 1
                : new Date().getFullYear()}
            </span>
          </div>
          <br />
        </div>
        {transfer && transfer.iraContribution && transfer.iraContribution.previousYearContributionAllowed && (
          <div className="col-6">
            <div>
              <span className="prop-title">Apply contribution to prior year? ({new Date().getFullYear() - 1}):</span>{' '}
              <span className="mute">
                {transfer && transfer.iraContribution && transfer.iraContribution.applyToPriorYear === true
                  ? 'Yes'
                  : 'No'}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderDepositMethod = () => (
    <>
      {transfer && transfer.mechanism === 'Ach' && (
        <>
          <>
            <span>Linked bank account</span>
            <br />
            {achRelationship && getFormattedAchRelationship(achRelationship)}
          </>
          {adminReview && transfer.id && <CashieringEntityOwnerNames id={transfer.id} type="transfers" />}
          {adminReview && achRelationship && isNotYetCreatedOrPendingStatus(achRelationship.state) && (
            <p className="warn">This relationship is pending.</p>
          )}
        </>
      )}
      {transfer && transfer.mechanism === 'Check' && (
        <>
          <span>Mail a check to the following address:</span>
          <br />
          <span className="mute">{nickname}</span>
          <br />
          <label>Delivery method:</label> <span className="mute">{transfer.deliveryMethod || '--'}</span>
          <br />
          <label>Memo to print on the check:</label>
          {_.some(transfer.memos, m => !!m) ? (
            _.map(_.filter(transfer.memos, m => !!m), (m, i) => (
              <div key={`memo${i}`} className="mb-3 mute">
                {m}
              </div>
            ))
          ) : (
            <div className="mute">--None--</div>
          )}
          <br />
          <label>Deposit address:</label>
          <div style={{ marginLeft: 8 }} className="mute">
            {clearer === ClearerType.RQD ? (
              <>
                <div>RQD Clearing LLC</div>
                <div>250 VESEY STREET</div>
                <div>SUITE 2601</div>
                <div>NEW YORK, NY 10281 USA</div>
                <div>
                  <strong>FBO: {nickname}</strong>
                </div>
              </>
            ) : (
              <>
                <div>Apex Clearing Corporation</div>
                <div>350 N St Paul, Ste. 1400</div>
                <div>Dallas, TX 75201</div>
                <div>
                  <strong>FBO: {nickname}</strong>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {gotoNextView && (
        <p>
          <button type="button" className="btn-link" onClick={() => gotoNextView(CashieringView.Type)}>
            <i className="fal fa-pencil" /> Change
          </button>
        </p>
      )}
    </>
  );

  if (useGridLayout) {
    return (
      <>
        <GridContainer>
          <GridSection>
            <GridSectionHeader>
              <h3>
                <i className="fal fa-money-bill-wave" style={{ marginRight: '8px' }}></i>
                How much to deposit?
              </h3>
            </GridSectionHeader>
            <GridSectionContent>{renderDepositAmount()}</GridSectionContent>
          </GridSection>

          <GridSection>
            <GridSectionHeader>
              <h3>
                <i className="fal fa-arrow-up" style={{ marginRight: '8px' }}></i>
                How to deposit the funds?
              </h3>
            </GridSectionHeader>
            <GridSectionContent>{renderDepositMethod()}</GridSectionContent>
          </GridSection>

          {transfer && transfer.frequencyInterval && transfer.frequencyUnit && (
            <GridSection>
              <GridSectionHeader>
                <h3>
                  <i className="fal fa-calendar-alt" style={{ marginRight: '8px' }}></i>
                  Scheduled Transfer Details
                </h3>
              </GridSectionHeader>
              <GridSectionContent>{renderScheduledTransferDetails()}</GridSectionContent>
            </GridSection>
          )}

          {transfer && transfer.iraContribution && (
            <GridSection>
              <GridSectionHeader>
                <h3>
                  <i className="fal fa-file-invoice-dollar" style={{ marginRight: '8px' }}></i>
                  IRA Contribution
                </h3>
              </GridSectionHeader>
              <GridSectionContent>{renderIRAContribution()}</GridSectionContent>
            </GridSection>
          )}
        </GridContainer>
        {children}
      </>
    );
  }

  // Legacy vertical layout
  return (
    <>
      <LegacySection className="screen-body-section">
        <p className="fields-title">How much to deposit?</p>
        {renderDepositAmount()}
      </LegacySection>

      <LegacySection className="screen-body-section">
        <p className="fields-title">How {adminReview ? '' : 'would you like'} to deposit the funds?</p>
        {renderDepositMethod()}
      </LegacySection>

      {transfer && transfer.frequencyInterval && transfer.frequencyUnit && (
        <LegacySection className="screen-body-section">
          <div className="container p-0 mb-3">
            <p className="fields-title">Scheduled Transfer Details</p>
            {renderScheduledTransferDetails()}
          </div>
        </LegacySection>
      )}

      {transfer && transfer.iraContribution && (
        <LegacySection className="screen-body-section">
          <div className="container p-0 mb-3">
            <p className="fields-title">IRA Contribution</p>
            {renderIRAContribution()}
          </div>
        </LegacySection>
      )}

      {children}
    </>
  );
};
